const __RELATIVE_SOURCE_FILE__ /* __INJECTED__ */ = 'libs/common_web/src/hooks/useDampenedWheelScroll.ts';
import { RefObject, useEffect } from 'react';

type Options = {
    /** Multiplier applied to wheel deltas. < 1 scrolls slower than native. Default 0.3. */
    factor?: number;
    enabled?: boolean;
    /** Skip damping while ctrl/cmd is held (the browser pinch-zoom gesture). Default true. */
    ignoreZoomModifier?: boolean;
};

/**
 * Slows mouse-wheel / trackpad scrolling on a scrollable element by taking over the wheel
 * (preventDefault + manual scroll) and applying a factor < 1
 */
export function useDampenedWheelScroll(
    elementRef: RefObject<HTMLElement | null>,
    { factor = 0.3, enabled = true, ignoreZoomModifier = true }: Options = {}
) {
    useEffect(() => {
        const el = elementRef.current;
        if (!el || !enabled) return;

        const onWheel = (e: WheelEvent) => {
            if (ignoreZoomModifier && (e.ctrlKey || e.metaKey)) return;
            e.preventDefault();
            // Normalize line/page deltas to pixels so the factor behaves the same across browsers.
            const unit = e.deltaMode === 1 ? 16 : e.deltaMode === 2 ? el.clientHeight : 1;
            el.scrollTop += e.deltaY * unit * factor;
            el.scrollLeft += e.deltaX * unit * factor;
        };

        el.addEventListener('wheel', onWheel, { passive: false });
        return () => el.removeEventListener('wheel', onWheel);
    }, [factor, enabled, ignoreZoomModifier]);
}
